import {
  AdminCustomSelect,
  AdminInput,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { ModalPortal } from '../../../components/modal/modalPortal';
import {
  BrandMenuResponse,
  CreateBrandMenuDto,
} from '../../../types/api/menu.types';
import { MENU_NAME_MAX_LENGTH, menuNameTaken } from '../helpers/brandMenus';

const EMPTY_MENU = 0;

type Props = {
  isOpen: boolean;
  mode: 'create' | 'rename';
  closeModal: () => void;
  menus: BrandMenuResponse[];
  /** The menu being renamed; ignored when creating. */
  current: BrandMenuResponse | null;
  isPos: boolean;
  onCreate: (dto: CreateBrandMenuDto) => Promise<boolean>;
  onRename: (id: number, name: string) => Promise<boolean>;
};

/**
 * One modal for both "new menu" (name + what to start from) and "rename"
 * (name only). Uniqueness is checked here against the list we already have
 * so the common mistake never reaches the backend; the backend still answers
 * 400 for a race.
 */
export const MenuFormModal = ({
  isOpen,
  mode,
  closeModal,
  menus,
  current,
  isPos,
  onCreate,
  onRename,
}: Props) => {
  const [name, setName] = useState('');
  const [copyFrom, setCopyFrom] = useState<number>(EMPTY_MENU);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(mode === 'rename' ? current?.name ?? '' : '');
    setCopyFrom(EMPTY_MENU);
  }, [isOpen, mode, current]);

  const isRename = mode === 'rename';

  const startFromOptions = [
    { name: 'Empty menu', value: EMPTY_MENU },
    ...menus.map((m) => ({ name: `Copy of ${m.name}`, value: m.id })),
  ];

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return ErrorToast('Please enter a menu name.');
    if (trimmed.length > MENU_NAME_MAX_LENGTH)
      return ErrorToast(
        `A menu name can have at most ${MENU_NAME_MAX_LENGTH} characters.`
      );
    if (menuNameTaken(menus, trimmed, isRename ? current?.id : null))
      return ErrorToast(`Menu "${trimmed}" already exists.`);

    setLoading(true);
    const ok = isRename
      ? current
        ? await onRename(current.id, trimmed)
        : false
      : await onCreate(
          copyFrom === EMPTY_MENU
            ? { name: trimmed }
            : { name: trimmed, copyFromMenuId: copyFrom }
        );
    setLoading(false);
    if (ok) closeModal();
  };

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      header={isRename ? 'Rename Menu' : 'New Menu'}
      width="480px"
      overflow="visible"
    >
      <AdminInput
        label="Menu name"
        value={name}
        onChange={({ target }) => setName(target.value)}
        placeholder="e.g. Uber Eats menu"
        maxLength={MENU_NAME_MAX_LENGTH}
      />

      {!isRename && (
        <>
          <Div $mt="16">
            <AdminCustomSelect<number>
              label="Start from"
              value={copyFrom}
              onChange={(value) => {
                if (typeof value === 'number') setCopyFrom(value);
              }}
              options={startFromOptions}
              $shadowDesign={false}
            />
          </Div>
          <Disclaimer $mt="12" $mb="0" $center>
            {copyFrom === EMPTY_MENU
              ? isPos
                ? 'An empty menu is filled by importing categories from the POS.'
                : 'Start blank, or pick a menu above to copy its categories, items, modifiers, schedules and prices.'
              : 'The copy keeps the same availability as the original: an item sold out in one menu is sold out in the other.'}
          </Disclaimer>
        </>
      )}

      <Button
        $selected
        $w="100%"
        $mt="32"
        $loading={loading}
        disabled={loading}
        onClick={handleSubmit}
      >
        {isRename ? 'Save' : 'Create Menu'}
      </Button>
    </ModalPortal>
  );
};
